import React, { useState } from "react";

import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import WipIncomingSubTable1 from "./incomingSubtables/WipIncomingSubTable1";
import { isReadOnlyUser } from "../../../shared/utility";
import NumberInputText from "../../controls/inputs/NumberInputText";
import { useCellSaveState } from "../../../hooks/useCellSaveState";
import TraceableLotNumber, {
  getInventoryLotTraceType,
} from "../../controls/TraceableLotNumber";

export default function IncomingWipItemsTableComponent(props) {
  const {
    loading,
    values,
    openSlideForManufacturedItemDetails,
    onAskReturnIncomingItem,
    incomingSelectedItems = [],
    setIncomingSelectedItems = () => {},
    askReturnSelectedIncomingItems,
    loadingRows,
    rowDetails,
    fetchSubRowDetails,
    subRowDetails,
    subLoadingRows,
    fetchRowDetails,
    onWipItemTotalCostEditComplete,
  } = props;

  const [expandedRows, setExpandedRows] = useState(null);
  const { getSaveStatus, saveCell } = useCellSaveState();

  const isReadOnly = isReadOnlyUser();
  const canEditTotalCost =
    !isReadOnly && typeof onWipItemTotalCostEditComplete === "function";

  const allowExpansion = (rowData) => {
    return true;
  };

  const onRowExpand = (event) => {
    const rowData = event.data;
    if (!rowDetails[rowData.unique_id]) {
      fetchRowDetails(rowData);
    }
  };

  const bodyProductName = (item) => {
    return (
      <p>
        {item.product_name} <br />
        {item.plu}
      </p>
    );
  };

  const bodyParentTotalWeight = (item) => {
    return <p>{item.total_weight_in_kg} kg</p>;
  };

  const bodyParentTotalCost = (item) => {
    const cellKey = `incoming:${item.unique_id}:total_cost`;

    return (
      <NumberInputText
        value={item.total_cost}
        aria-label={`Total cost for ${item.product_name || item.plu}`}
        className="editable-table-number"
        inputClassName="editable-table-input"
        disabled={!canEditTotalCost}
        saveStatus={getSaveStatus(cellKey)}
        onCommit={(totalCost) =>
          saveCell(cellKey, () =>
            onWipItemTotalCostEditComplete({
              ...item,
              total_cost: totalCost,
            }),
          )
        }
        mode="currency"
        currency="USD"
        locale="en-US"
        minFractionDigits={2}
        maxFractionDigits={2}
        min={0}
      />
    );
  };

  const bodyParentTotalPieces = (item) => {
    return <p>{item.total_pieces} pieces</p>;
  };

  const rowExpansionTemplate = (data) => {
    return (
      <WipIncomingSubTable1
        data={data}
        loadingRows={loadingRows}
        rowDetails={rowDetails}
        fetchSubRowDetails={fetchSubRowDetails}
        subRowDetails={subRowDetails}
        subLoadingRows={subLoadingRows}
        openSlideForManufacturedItemDetails={
          openSlideForManufacturedItemDetails
        }
        onAskReturnIncomingItem={onAskReturnIncomingItem}
        incomingSelectedItems={incomingSelectedItems}
        setIncomingSelectedItems={setIncomingSelectedItems}
      />
    );
  };

  return (
    <div className="flow-root">
      <div className="overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8 space-y-2">
          <div className="w-full flex items-center">
            <h3 className="text-lg leading-6 font-medium text-gray-900 flex-grow">
              Incoming items
            </h3>
            {!isReadOnly && askReturnSelectedIncomingItems ? (
              <button
                onClick={askReturnSelectedIncomingItems}
                className="rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600"
              >
                Return selected ({(incomingSelectedItems || []).length})
              </button>
            ) : null}
          </div>
          {loading ? (
            <div>
              <div className="border h-10 shadow rounded-md p-4 w-full mx-auto">
                <div className="animate-pulse flex space-x-4">Loading</div>
              </div>
            </div>
          ) : (
            <div className="overflow-hidden card-custom bg-white">
              <DataTable
                value={values.incoming_items}
                expandedRows={expandedRows}
                onRowToggle={(e) => {
                  setExpandedRows(e.data);
                }}
                onRowExpand={onRowExpand}
                rowExpansionTemplate={rowExpansionTemplate}
                dataKey="unique_id"
                tableClassName="min-w-full bg-white divide-y divide-gray-300 text-sm wip-incoming-items-table"
                cellClassName="p-2 border-1-bottom-gray text-sm"
              >
                <Column
                  header="Product Name"
                  body={bodyProductName}
                  style={{ width: "25%" }}
                />
                <Column
                  header="Lot Number"
                  body={(item) => (
                    <TraceableLotNumber
                      lotNumber={item.lot_number}
                      traceType={getInventoryLotTraceType(item)}
                    />
                  )}
                  style={{ width: "10%" }}
                />
                <Column
                  header="Date"
                  field="date"
                  style={{ textAlign: "start", width: "10%" }}
                />
                <Column
                  header="Carton Count"
                  field="carton_count"
                  style={{ textAlign: "end", width: "15%" }}
                />
                <Column
                  header="Total Weight (kg)"
                  body={bodyParentTotalWeight}
                  style={{ textAlign: "end", width: "10%" }}
                />
                <Column
                  header="Total Pieces"
                  body={bodyParentTotalPieces}
                  style={{ textAlign: "end", width: "10%" }}
                />
                <Column
                  header="Total Cost"
                  field="total_cost"
                  body={bodyParentTotalCost}
                  style={{ textAlign: "end", width: "10%" }}
                />
                <Column expander={allowExpansion} style={{ width: "6%" }} />
              </DataTable>
              <div className="flex justify-end p-4 gap-4">
                {values.incoming_info ? (
                  <>
                    <p className="text-md font-bold">
                      Total cartons: {values.incoming_info.carton_count}
                    </p>
                    <p>
                      Total weight: {values.incoming_info.total_weight_in_kg} kg
                    </p>
                    <p>
                      Total pieces: {values.incoming_info.total_pieces} pieces
                    </p>
                    <p>Total cost: ${values.incoming_info.total_cost}</p>
                  </>
                ) : null}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
